import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import useSetFocusGroup from 'hooks/useSetFocusGroup';
import AutoExport from 'groups/AutoExport';
import Beneficiaries from 'groups/Beneficiaries';
import BusinessUsers from 'groups/BusinessUsers';
import Default from 'groups/Default';
import Documents from 'groups/Documents';
import Done from 'groups/Done';
import EsignAgreements from 'groups/EsignAgreements';
import EsignDisclosures from 'groups/EsignDisclosures';
import FundingAmount from 'groups/FundingAmount';
import FundingCC from 'groups/FundingCC';
import FundingExtVerification from 'groups/FundingExtVerification';
import FundingTransferCheck from 'groups/FundingTransferCheck';
import FundingTransferExt from 'groups/FundingTransferExt';
import FundingTransferInt from 'groups/FundingTransferInt';
import Kba from 'groups/Kba';
import OffersQualified from 'groups/OffersQualified';
import PrimaryUser from 'groups/PrimaryUser';
import Dl from 'groups/Dl';
import Qualify from 'groups/Qualify';

const styles = ({ forms, screenSizes, spacing, typography }) => ({
  description: {
    marginBottom: spacing.lg,
    textAlign: 'center',
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      '&.active': {
        display: 'block',
      },
      display: 'none',
      marginBottom: spacing.md,
    },
  },
  forceShow: {},
  listGroupView: {
    margin: `${[0, 'auto', 30, 'auto']} !important`,
    maxWidth: '440px !important',
  },
  root: {
    '& .fields-view': {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    '&$forceShow': {
      display: 'block',
    },
    '&.active': {
      display: 'block',
      marginTop: 0,
    },
    '&.low-pad': {
      padding: '0 3%',
    },
    '&:focus': {
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
    },
    display: 'block',
    margin: [spacing.lg, 'auto', 0],
    marginTop: ({ description, title }) => (title && description ? 0 : undefined),
    maxWidth: forms.maxWidth,
    padding: 0,
    // position: 'relative',
    width: '100%',
  },
  title: {
    ...typography.h1,
    marginBottom: spacing.sm,
    textAlign: 'center',
    textTransform: 'uppercase',
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      '&.active': {
        display: 'block',
      },
      display: 'none',
      marginBottom: spacing.xs,
    },
  },
  [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
    root: {
      '&$forceShow': {
        display: 'block',
      },
      display: 'none',
      padding: [0, spacing.sm],
    },
  },
  [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
    root: {},
  },
});

const getClassName = (name, desktop, mobile) => {
  let output = '';
  switch (name) {
    case 'START_NEW':
    case 'START_EXISTING':
      output += 'col-lg-6 low-pad';
      break;
    default:
      output += 'col-lg-12';
      break;
  }

  if (desktop === false) {
    output += ' desktop-hidden';
  }

  if (mobile === false) {
    output += ' mobile-hidden';
  }

  output += ` ${name.toLowerCase()}-group-view`;

  return output;
};

const AnnounceStatusIfNoButton = ({ children, name = '' }) => {
  if (name.toLowerCase() === 'qualify' || name.toLowerCase() === 'autoexport') {
    return <div role="alert">{children}</div>;
  }

  return children;
};

const Group = ({
  activeGroup,
  children,
  classes,
  description,
  hasFields,
  isAgreement,
  isDesktop,
  isDisclosure,
  isMobile,
  name,
  pageName,
  title,
}) => {
  const groupProps = {
    children,
    name,
  };

  useSetFocusGroup(name, activeGroup);
  const active = activeGroup === name;
  /** this is another hack */
  const forceMobile = name.startsWith('FUNDING') || pageName === 'launch';
  /** Note: title and description are hidden for non-theme.json clients */

  return (
    <>
      <AnnounceStatusIfNoButton name={name}>
        {title && classes.title ? (
          <p className={classnames(classes.title, { active, hidden: forceMobile && !active })}>
            {title}
          </p>
        ) : null}
        {description && classes.description ? (
          <p
            className={classnames(classes.description, { active, hidden: forceMobile && !active })}
          >
            {description}
          </p>
        ) : null}
      </AnnounceStatusIfNoButton>
      <fieldset
        id={name}
        data-name={name}
        className={classnames(
          getClassName(name, isDesktop, isMobile),
          {
            active,
            [classes.forceShow]:
              isAgreement ||
              isDisclosure ||
              pageName === 'productoptions' ||
              pageName === 'beneficiaries',
            [classes.listGroupView]: pageName === 'beneficiaries' && name === 'ENABLE_BENEFICIARY',
          },
          classes.root,
        )}
        role={!hasFields ? 'presentation' : undefined}
      >
        {(() => {
          if (isAgreement) {
            return <EsignAgreements {...groupProps} />;
          }
          if (isDisclosure) {
            return <EsignDisclosures {...groupProps} />;
          }
          /* istanbul ignore next */
          switch (name.toLowerCase()) {
            case 'auto_export':
              return <AutoExport {...groupProps} />;
            case 'beneficiaries':
              return <Beneficiaries {...groupProps} />;
            case 'primary_user':
              return <PrimaryUser {...groupProps} />;
            case 'business_users':
              return <BusinessUsers {...groupProps} />;
            case 'documents':
              return <Documents {...groupProps} />;
            case 'done_confirmation':
            case 'done_declined':
            case 'done_declined_adverse_action':
            case 'done_declined_kba':
            case 'done_review':
            case 'done_awaiting_joint':
            case 'done_finished':
              return <Done {...groupProps} />;
            case 'funding_amount':
              return <FundingAmount {...groupProps} />;
            case 'funding_cc':
              return <FundingCC {...groupProps} />;
            case 'funding_ext_verification':
              return <FundingExtVerification {...groupProps} />;
            case 'funding_transfer_check':
              return <FundingTransferCheck {...groupProps} />;
            case 'funding_transfer_ext':
              return <FundingTransferExt {...groupProps} />;
            case 'funding_transfer_int':
              return <FundingTransferInt {...groupProps} />;
            case 'kba':
              return <Kba {...groupProps} />;
            case 'offers_declined_with_crosssale':
              return <OffersQualified {...groupProps} isDeclinedView />;
            case 'offers_qualified':
              return <OffersQualified {...groupProps} />;
            case 'primary_dl':
              return (
                <Dl
                  {...groupProps}
                  wrapClass="primary-dl-group-view"
                  keyboardGroup="PRIMARY_NAME"
                />
              );
            case 'secondary_dl':
              return (
                <Dl
                  {...groupProps}
                  wrapClass="secondary-dl-group-view"
                  keyboardGroup="SECONDARY_NAME"
                />
              );
            case 'signer_dl':
              return (
                <Dl
                  {...groupProps}
                  wrapClass="secondary-dl-group-view"
                  keyboardGroup="SIGNER_NAME"
                />
              );
            case 'qualify':
              return <Qualify {...groupProps} />;
            default:
              return <Default {...groupProps} />;
          }
        })()}
      </fieldset>
    </>
  );
};

Group.propTypes = {
  activeGroup: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  description: PropTypes.string,
  hasFields: PropTypes.bool,
  isAgreement: PropTypes.bool.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isDisclosure: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  pageName: PropTypes.string,
  title: PropTypes.string,
};

Group.defaultProps = {
  activeGroup: '',
  description: '',
  hasFields: false,
  pageName: '',
  title: '',
};

const mapStateToProps = ({ groups, page, ui }, { forceActiveGroup = null, name }) => {
  const group = groups[name];
  /**
   * This is hack because we don't use group types even though they come from the API
   * Apparently they are not valid
   */
  const agreementRegex = /agreement/i;
  const isEsign = page.name === 'esign';
  const isAgreement = isEsign && agreementRegex.test(name);
  return {
    activeGroup: forceActiveGroup || ui.activeGroup,
    description: group?.description,
    hasFields: group?.fields?.length > 0 ?? false,
    isAgreement,
    isDesktop: group?.desktop ?? true,
    isDisclosure: !isAgreement && isEsign,
    isMobile: group?.mobile ?? false,
    pageName: page.name,
    title: group?.title,
  };
};

const GroupStyled = withStyles(styles)(Group);

export default connect(mapStateToProps)(GroupStyled);
